/**
 * The seven reads the application flow renders itself against, and one reload for all of them.
 *
 * WHY THEY ARE LOADED TOGETHER RATHER THAN PER-COMPONENT. Because they contradict each other if
 * they are not: `getApplication` is a join over the profile, the terms acceptance and the queue
 * item, so a screen holding a fresh application beside a stale profile shows a checklist that
 * disagrees with the form underneath it. Saving one section reloads the pair.
 *
 * TWO OF THE SIX ARE ALLOWED TO FAIL WITHOUT TAKING THE PAGE DOWN — the category catalogue and the
 * credential list. Neither gates anything: expertise can be re-tried in place, and identity
 * evidence is worth five weight points and blocks nothing. Their failure is rendered where they
 * are, not as a full-page error, because a page that refuses to draw because an optional list
 * would not load is a page that has confused its own priorities.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from '../../../i18n';
import type {
  Application,
  AuthorSession,
  CategoryList,
  CredentialList,
  OwnProfile,
  TermsState,
} from '../contract';
import { useApplicationApi } from './provider';
import { useAsyncResource } from './useAsyncResource';
import type { AsyncResource } from './useAsyncResource';

export interface ApplicationData {
  /** 'loading' until the four load-bearing reads have all answered once. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The first failure among the four load-bearing reads. */
  readonly error: unknown;
  readonly isRefreshing: boolean;

  readonly session: AuthorSession | undefined;
  readonly application: Application | undefined;
  readonly profile: OwnProfile | undefined;
  readonly terms: TermsState | undefined;

  /** Optional, and its own failure is rendered in place. */
  readonly categories: AsyncResource<CategoryList>;
  /** Optional, same treatment. */
  readonly credentials: AsyncResource<CredentialList>;

  /** Reloads everything. */
  readonly reload: () => void;
  /** Reloads only what a profile write can have changed: the profile and the application. */
  readonly reloadProfileAndApplication: () => void;
  /** Hands a mutation's own response straight in, so the screen does not flash while it refetches. */
  readonly setProfile: (value: OwnProfile) => void;
  readonly setApplication: (value: Application) => void;
  readonly setTerms: (value: TermsState) => void;
}

export function useApplicationData(): ApplicationData {
  const api = useApplicationApi();
  // The category names come in the cabinet's language, so a change of language reads them again.
  const { i18n } = useTranslation('application');

  const session = useAsyncResource<AuthorSession>(
    useCallback((signal) => api.getAuthorSession({ signal }), [api]),
  );
  const application = useAsyncResource<Application>(
    useCallback((signal) => api.getApplication({ signal }), [api]),
  );
  const profile = useAsyncResource<OwnProfile>(
    useCallback((signal) => api.getOwnProfile({ signal }), [api]),
  );
  const terms = useAsyncResource<TermsState>(
    useCallback((signal) => api.getCurrentTerms({ signal }), [api]),
  );
  const categories = useAsyncResource<CategoryList>(
    useCallback((signal) => api.listEditorCategories({ signal }), [api]),
    i18n.language,
  );
  const credentials = useAsyncResource<CredentialList>(
    useCallback((signal) => api.listOwnCredentials({ signal }), [api]),
  );
  const agreements = useAsyncResource<AgreementList>(
    useCallback((signal) => api.listAgreements({ signal }), [api]),
  );

  const core = [session, application, profile, terms];
  // Its first answer, either way: the last step must not draw the agreement alone and then grow.
  const agreementsAnswered = agreements.data !== undefined || agreements.error !== undefined;

  const status: ApplicationData['status'] = core.some((r) => r.status === 'error')
    ? 'error'
    : core.every((r) => r.status === 'ready') && agreementsAnswered
      ? 'ready'
      : 'loading';

  const error = core.find((r) => r.error !== undefined)?.error;
  const isRefreshing = core.some((r) => r.isRefreshing);

  const reload = useCallback(() => {
    session.reload();
    application.reload();
    profile.reload();
    terms.reload();
    categories.reload();
    credentials.reload();
    agreements.reload();
  }, [session, application, profile, terms, categories, credentials, agreements]);

  const reloadProfileAndApplication = useCallback(() => {
    profile.reload();
    application.reload();
  }, [profile, application]);

  return useMemo(
    () => ({
      status,
      error,
      isRefreshing,
      session: session.data,
      application: application.data,
      profile: profile.data,
      terms: terms.data,
      categories,
      credentials,
      agreements,
      reload,
      reloadProfileAndApplication,
      setProfile: profile.set,
      setApplication: application.set,
      setTerms: terms.set,
    }),
    [
      status,
      error,
      isRefreshing,
      session.data,
      application.data,
      application.set,
      profile.data,
      profile.set,
      terms.data,
      terms.set,
      categories,
      credentials,
      agreements,
      reload,
      reloadProfileAndApplication,
    ],
  );
}

/*
 * ★ DESIGN 25: the documents and what this author has accepted, for the application's last step.
 * Waited for once, and optional after that: a list that will not load leaves the author agreement's
 * own read (`terms`) to answer for it (../domain/agreements.ts). Declared at the foot - the member
 * by declaration merging, the type's import here - so the lines above, cited by number, keep theirs.
 */
export interface ApplicationData {
  readonly agreements: AsyncResource<AgreementList>;
}

import type { AgreementList } from '../contract';
